import { useMemo, useRef, useState } from "react";

import {
  faArchive,
  faArrowDown,
  faArrowUp,
  faArrowUpArrowDown,
  faExclamationCircle,
  faLink,
  faMagnifyingGlass,
  faSuitcase,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Column,
  createColumnHelper,
  DisplayColumnDef,
  HeaderContext,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { Checkbox } from "src/base-components/Checkbox";
import { DisplayDateFromNow } from "src/base-components/Date";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { MENU_DIVIDER } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import {
  Cell,
  ColumnVisibilityDropdown,
  Header,
  TableComp as Table,
} from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { ArchiveCasesConfirmModal } from "src/cases/ArchiveCasesConfirmModal";
import { CaseAssignee } from "src/cases/common/CaseAssignee";
import { CaseStatusPillWithSchema } from "src/cases/common/CaseStatusPillWithSchema";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useAssignCase, useIsQueueMember } from "src/cases/hooks";
import { useBulkCaseUpdate } from "src/cases/queries/cases";
import { useQueueCases } from "src/cases/queries/queues";
import { useCasesTableContext } from "src/cases/queues/CasesTableContext";
import {
  CasePropertyValue,
  CaseTemplatePill,
  CaseTemplateStatusValue,
} from "src/cases/queues/CasesTableValues";
import { sortingStateToSortParam } from "src/cases/queues/hooks/filterParamUtils";
import { useCasesQueueParams } from "src/cases/queues/hooks/useCasesQueueParams";
import { useEntityDisplayMap } from "src/cases/queues/hooks/useEntityDisplayMap";
import { useQueueColumnSchemas } from "src/cases/queues/hooks/useQueueColumnSchemas";
import {
  CaseQueueColumnSchemas,
  DeserializedQueueCase,
  EnrichedDeserializedQueueCase,
} from "src/cases/queues/types";
import {
  enrichCaseWithColumnValues,
  isColumnSortable,
  RESERVED_PROPERTY_KEYS,
  sortingToApiSortParam,
} from "src/cases/queues/utils";
import { onArchiveCasesError } from "src/cases/utils";
import { EmptyState } from "src/design-system/EmptyState";
import { IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import {
  CasesPageParams,
  getBaseUrl,
  getUrlToCaseViewPage,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

const helper = createColumnHelper<DeserializedQueueCase>();

type BuildColumnParams = {
  columnSchemas: CaseQueueColumnSchemas;
  environment: Environment;
  showSelectorColumn: boolean;
  isSelectionDisabled?: boolean;
  canArchive: boolean;
  onRequestArchive: (caseId: string) => void;
};

const buildColumns = ({
  columnSchemas,
  environment,
  showSelectorColumn,
  isSelectionDisabled = false,
  canArchive,
  onRequestArchive,
}: BuildColumnParams) => {
  const casePropertyColumns = Object.entries(columnSchemas)
    ?.map(([property, columnSchema]) => {
      if (!columnSchema || RESERVED_PROPERTY_KEYS.includes(columnSchema.__key))
        return null;

      return helper.accessor(
        (row) => {
          if (columnSchema.template_version_ids.has(row.template_version.id)) {
            return row.case_properties[columnSchema.__key];
          }
          return undefined;
        },
        {
          id: property,
          enableSorting: isColumnSortable(columnSchema),
          meta: {
            displayName: columnSchema.display_name,
          },
          header: (info) => (
            <Header
              dataLoc={`cases-table-header-${columnSchema.__key}`}
              info={info}
            >
              <HeaderWithSort info={info} title={columnSchema.display_name} />
            </Header>
          ),
          cell: (info) => {
            return (
              <Cell
                dataLoc={`cases-table-cell-${columnSchema.__key}-${info.row.id}`}
                info={info}
              >
                <CasePropertyValue
                  columnSchema={columnSchema}
                  info={info}
                  environment={environment}
                />
              </Cell>
            );
          },
          maxSize: 350,
        },
      );
    })
    .filter(Boolean) as DisplayColumnDef<
    EnrichedDeserializedQueueCase,
    unknown
  >[];

  const caseStatusColumn = helper.accessor((row) => row, {
    id: "status",
    enableSorting: false,
    meta: { displayName: "Status" },
    header: (info) => (
      <Header dataLoc="cases-table-header-status" info={info}>
        Status
      </Header>
    ),
    cell: (info) => {
      const caseData = info.row.original;

      return (
        <Cell dataLoc={`cases-table-cell-status-${info.row.id}`} info={info}>
          <CaseStatusPillWithSchema
            templateId={caseData.template.id}
            versionId={caseData.template_version.id}
            status={caseData.case_properties.status}
            subStatus={caseData.case_properties?.sub_status}
          />
        </Cell>
      );
    },
    maxSize: 350,
  });

  const caseTemplateColumns = [
    helper.accessor("template.id", {
      enableSorting: false,
      meta: { displayName: "Template" },
      header: (info) => (
        <Header dataLoc="cases-table-header-template" info={info}>
          Template
        </Header>
      ),
      cell: (info) => {
        const caseData = info.row.original;

        return (
          <Cell
            className="flex gap-x-1"
            dataLoc={`cases-table-cell-template-${info.row.id}`}
            info={info}
          >
            <CaseTemplatePill
              templateId={caseData.template.id}
              versionId={caseData.template_version.id}
            />
          </Cell>
        );
      },
      maxSize: 250,
    }),
    helper.accessor(
      (row) => ({
        templateId: row.template?.id,
        schemaVersion: row.template_schema_version,
        versionId: row.template_version?.id,
        queueId: row.queue?.id,
      }),
      {
        id: "template_status",
        enableSorting: false,
        meta: { displayName: "Template Status" },
        header: (info) => (
          <Header info={info}>
            <span className="whitespace-nowrap">Template Status</span>
          </Header>
        ),
        cell: (info) => (
          <Cell info={info}>
            <CaseTemplateStatusValue value={info.getValue()} />
          </Cell>
        ),
      },
    ),
  ];

  const caseDatesColumns = [
    helper.accessor("created_at", {
      enableSorting: true,
      meta: { displayName: "Created at" },
      header: (info) => (
        <Header dataLoc="cases-table-header-created-at" info={info}>
          <HeaderWithSort info={info} title="Created At" />
        </Header>
      ),
      cell: (info) => (
        <Cell className="font-inter-normal-12px" info={info}>
          <DisplayDateFromNow value={info.getValue()} />
        </Cell>
      ),
    }),
    helper.accessor("updated_at", {
      enableSorting: true,
      meta: { displayName: "Updated at" },
      header: (info) => (
        <Header dataLoc="cases-table-header-updated-at" info={info}>
          <HeaderWithSort info={info} title="Updated At" />
        </Header>
      ),
      cell: (info) => (
        <Cell className="font-inter-normal-12px" info={info}>
          <DisplayDateFromNow value={info.getValue()} />
        </Cell>
      ),
    }),
  ];

  const caseAssigneeColumn = helper.accessor("assignee", {
    enableSorting: false,
    meta: { displayName: "Assigned to" },
    header: (info) => (
      <Header dataLoc="cases-table-header-assigned-to" info={info}>
        Assigned to
      </Header>
    ),
    cell: (info) => {
      const data = info.row.original;
      const queueId = data.queue?.id;

      return (
        <Cell info={info}>
          <div onClick={(e) => e.stopPropagation()}>
            {queueId && (
              <CaseAssignee
                assignee={data.assignee?.id}
                caseId={data.id}
                etag={data.etag}
                queueId={queueId}
              />
            )}
          </div>
        </Cell>
      );
    },
    maxSize: 200,
  });

  const idColumn = helper.accessor("id", {
    enableSorting: false,
    meta: { displayName: "Case ID" },
    header: (info) => (
      <Header dataLoc="cases-table-header-id" info={info}>
        <span className="whitespace-nowrap">Case ID</span>
      </Header>
    ),
    cell: (info) => (
      <Cell className="w-24" info={info}>
        <TruncatedIdPill id={info.getValue()} variant="last-five" />
      </Cell>
    ),
  });

  const selectorColumn = helper.display({
    id: "select",
    meta: { sticky: "left" },
    header: (info) => (
      <Header dataLoc="cases-table-header-select" info={info}>
        <Checkbox
          checked={info.table.getIsAllRowsSelected()}
          disabled={isSelectionDisabled}
          indeterminate={info.table.getIsSomePageRowsSelected()}
          onChange={info.table.getToggleAllRowsSelectedHandler()}
        />
      </Header>
    ),
    cell: (info) => (
      <Cell
        className="w-8 items-center"
        dataLoc={`cases-table-cell-select-${info.row.id}`}
        info={info}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={info.row.getIsSelected()}
            disabled={!info.row.getCanSelect() || isSelectionDisabled}
            onChange={info.row.getToggleSelectedHandler()}
          />
        </div>
      </Cell>
    ),
  });

  const actionsColumn = helper.display({
    id: "actions",
    header: (info) => (
      <Header dataLoc="cases-table-header-actions" info={info}>
        <div className="flex w-13.5 justify-end bg-white pr-1.5">
          <ColumnVisibilityDropdown min={3} table={info.table} />
        </div>
      </Header>
    ),
    cell: (info) => {
      const data = info.row.original;

      return (
        <Cell className="items-center justify-end" info={info}>
          <CaseActionsMenu
            assigneeId={data.assignee?.id}
            caseId={data.id}
            etag={data.etag}
            canArchive={canArchive}
            onRequestArchive={() => onRequestArchive(data.id)}
          />
        </Cell>
      );
    },
    size: 40,
    maxSize: 40,
    meta: { sticky: "right" },
  });

  return [
    ...(showSelectorColumn ? [selectorColumn] : []),
    idColumn,
    ...caseTemplateColumns,
    ...casePropertyColumns,
    caseStatusColumn,
    caseAssigneeColumn,
    ...caseDatesColumns,
    actionsColumn,
  ] as DisplayColumnDef<EnrichedDeserializedQueueCase, unknown>[];
};

const SortToggle: React.FC<{
  column: Column<DeserializedQueueCase, unknown>;
}> = ({ column }) => {
  const isSorted = column.getIsSorted();
  const icon =
    isSorted === "asc"
      ? faArrowUp
      : isSorted === "desc"
        ? faArrowDown
        : faArrowUpArrowDown;

  return (
    <IconButton
      dataLoc={`sort-toggle-${column.id}`}
      icon={icon}
      size="2xs"
      isActive={!!isSorted}
      onClick={column.getToggleSortingHandler()}
    />
  );
};

const HeaderWithSort: React.FC<{
  title: string;
  info: HeaderContext<DeserializedQueueCase, unknown>;
}> = ({ title, info }) => (
  <div className="flex items-center gap-x-1 whitespace-nowrap capitalize">
    {title}
    {info.column.getCanSort() && <SortToggle column={info.column} />}
  </div>
);

type CaseActionsMenuProps = {
  caseId: string;
  etag: string;
  assigneeId?: string;
  canArchive: boolean;
  onRequestArchive: () => void;
};

const CaseActionsMenu: React.FC<CaseActionsMenuProps> = ({
  caseId,
  etag,
  assigneeId,
  canArchive,
  onRequestArchive,
}) => {
  const { wsId, orgId } = useParamsDecode<CasesPageParams>();
  const { queue_id } = useParamsDecode<CasesPageParams>();

  const currentUserId = useCurrentUserId();
  const { onAssign: assignCase } = useAssignCase(queue_id);

  const onCopyLink = async () => {
    try {
      await copyTextToClipboard(
        getBaseUrl() + getUrlToCaseViewPage(orgId, wsId, caseId),
      );
      toastActions.success({
        title: "Link copied to clipboard",
      });
    } catch {
      toastActions.failure({
        title: "Failed to copy link",
      });
    }
  };

  const isAssignedToCurrentUser = assigneeId === currentUserId;

  return (
    <EllipsisOptionsDropdown
      buttonClassName="opacity-0 group-hover:opacity-100"
      dataLoc="case-row-actions"
      elements={[
        {
          key: "Copy case link",
          icon: faLink,
          action: onCopyLink,
        },
        {
          key: "Claim case",
          icon: faSuitcase,
          disabled: isAssignedToCurrentUser,
          action: () =>
            assignCase({
              etag,
              caseId,
              assigneeId: currentUserId,
            }),
        },
        ...(canArchive
          ? [
              MENU_DIVIDER,
              {
                key: "Archive case",
                icon: faArchive,
                action: onRequestArchive,
              },
            ]
          : []),
      ]}
      iconSize="sm"
      placement="bottom start"
    />
  );
};

export const CasesTable: React.FC = () => {
  const navigate = useNavigate();
  const [environment] = useEnvironment();
  const { onSelectRow, selectedRows, isSelectionDisabled } =
    useCasesTableContext();
  const currentUserId = useCurrentUserId();
  const { cases: casesCapabilities } = useCapabilities();
  const { orgId, wsId, queue_id: queueId } = useParamsDecode<CasesPageParams>();
  const isQueueMember = useIsQueueMember({ userId: currentUserId, queueId });
  const canInteractWithCases = casesCapabilities.canManage || isQueueMember;
  const getPropertyIcon = useGetCasePropertyIcon();

  const [archiveTargetCaseId, setArchiveTargetCaseId] = useState<string | null>(
    null,
  );
  const { mutateAsync: updateCases } = useBulkCaseUpdate(queueId);

  const canArchive = environment === "sandbox" || casesCapabilities.canManage;

  const performArchive = async (caseId: string) => {
    try {
      await updateCases({
        queueId,
        operation: "archive",
        caseIds: [caseId],
      });
    } catch (error) {
      logger.error("Error archiving case", error);
      onArchiveCasesError(error);
    }
  };

  const requestArchive = (caseId: string) => {
    if (environment === "live") {
      setArchiveTargetCaseId(caseId);
      return;
    }
    return performArchive(caseId);
  };

  const { columnSchemas, isLoading: isLoadingColumnSchemas } =
    useQueueColumnSchemas(queueId);

  const {
    filterParams: filters,
    hasActiveFilter,
    sorting,
    onSortingChange,
    clearFilters,
  } = useCasesQueueParams(queueId);

  const {
    data,
    isLoading: isLoadingQueueCases,
    isError,
    fetchNextPage,
    isFetchingNextPage,
  } = useQueueCases({
    queueId,
    environment,
    filters,
    sort: sortingToApiSortParam(sorting),
  });

  const cases = useMemo(
    () =>
      data?.map((caseData) =>
        enrichCaseWithColumnValues(caseData, columnSchemas),
      ) ?? [],
    [data, columnSchemas],
  );

  const isLoading = isLoadingQueueCases || isLoadingColumnSchemas;

  const { displayMap } = useEntityDisplayMap({
    cases,
    columnSchemas,
    environment,
    enabled: !isLoading && cases.length > 0,
  });

  // Use ref to keep a stable reference to displayMap
  const entityDisplayMapRef = useRef<Record<string, string>>({});
  entityDisplayMapRef.current = displayMap;

  const columns = useMemo(() => {
    return buildColumns({
      columnSchemas,
      environment,
      showSelectorColumn: casesCapabilities.canAccess,
      isSelectionDisabled,
      canArchive,
      onRequestArchive: requestArchive,
    });
  }, [
    columnSchemas,
    environment,
    casesCapabilities.canAccess,
    isSelectionDisabled,
    canArchive,
  ]);

  const isCasesError = !isLoading && isError;
  const hasCases = !isLoading && !!cases.length;
  const isCasesEmpty = !isLoading && !isCasesError && !hasCases;
  const isCasesFilterEmpty = !hasActiveFilter;

  const sortParam = sortingStateToSortParam(sorting);

  return (
    <FloatingWindowsProvider>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
        <Table
          actions={{
            getEntityDisplayValue: (id: string): string =>
              entityDisplayMapRef.current[id],
            getPropertyIcon,
          }}
          columnVisibility={{
            template_status: false,
            template_id: false,
          }}
          columns={columns}
          data={cases}
          dataLoc="cases-table"
          manualSorting
          onSortingChange={onSortingChange}
          sorting={sorting}
          frameClassName={twJoin(
            "w-full pb-2",
            (isCasesEmpty || isCasesError) && "h-auto",
          )}
          getRowId={(row) => row.id}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          rowClassName={twJoin(
            canInteractWithCases && "cursor-pointer hover:bg-gray-50",
          )}
          rowPropsGetter={(row) =>
            canInteractWithCases
              ? {
                  "data-loc": `queue-case-row-${row.id}`,
                  onClick: () => {
                    navigate(
                      getUrlToCaseViewPage(orgId, wsId, row.id, {
                        ...filters,
                        ...(queueId ? { queueId } : {}),
                        ...(sortParam ? { sort: sortParam } : {}),
                      }),
                    );
                  },
                }
              : {}
          }
          rowSelection={selectedRows}
          uiStoreKey={`${wsId}_${queueId}_cases_visible_columns`}
          variant="compact"
          onRowSelectionChange={onSelectRow}
          onScrolledToTheBottom={fetchNextPage}
        />
        {isCasesError && (
          <div className="h-96">
            <EmptyState
              actionButton={{
                label: "Clear filters",
                onClick: clearFilters,
              }}
              description="We're having trouble loading cases. Try clearing the filters."
              headline="Error loading cases"
              icon={faExclamationCircle}
              variant="error"
            />
          </div>
        )}
        {isCasesEmpty && (
          <div className="h-96" data-loc="cases-table-empty-state">
            {isCasesFilterEmpty ? (
              <EmptyState
                description="No cases found in this queue"
                headline="No cases found"
                icon={faMagnifyingGlass}
              />
            ) : (
              <EmptyState
                actionButton={{
                  label: "Clear filters",
                  onClick: clearFilters,
                }}
                description="There are no cases matching your current filters. Try clearing the filters."
                headline="No cases for the current filters"
                icon={faMagnifyingGlass}
              />
            )}
          </div>
        )}
      </div>
      <ArchiveCasesConfirmModal
        caseCount={1}
        isOpen={archiveTargetCaseId !== null}
        onClose={() => setArchiveTargetCaseId(null)}
        onConfirm={async () => {
          if (archiveTargetCaseId !== null) {
            await performArchive(archiveTargetCaseId);
          }
          setArchiveTargetCaseId(null);
        }}
      />
    </FloatingWindowsProvider>
  );
};
